import type { EventClickArg } from '@fullcalendar/core';

import { getErrorStatus } from '@/common/error';
import type { StatusTone } from '@/components/StatusPill/StatusPill';
import { type CalendarItem, isCalendarAbsence, isCalendarTask } from '@/hooks/useCalendarFilter';
import { EVENT_TYPE_COLORS, type EventResponse, EVENTS } from '@/store/events/events.dto';
import { TASK_STATUSES, TaskStatus } from '@/store/tasks/tasks.dto';
import { toast } from '@/utils/toast';

// Date Utilities

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const isToday = (date: Date): boolean => {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

// Item Type Detection

export { isCalendarAbsence, isCalendarTask };

export const mapCalendarEventToEventResponse = (
  calendarEvent: EventClickArg['event'],
): EventResponse | null => {
  const item = calendarEvent.extendedProps as CalendarItem | undefined;
  if (!item || isCalendarTask(item) || isCalendarAbsence(item)) return null;
  return item;
};

export const isOutOfWater = (item: CalendarItem): boolean => item.type === EVENTS.OUT_OF_WATER;

// Status Presentation

export type StatusView = { label: string; tone: StatusTone };

const TASK_STATUS_TONE: Record<string, StatusTone> = {
  [TaskStatus.PENDING]: 'neutral',
  [TaskStatus.IN_PROGRESS]: 'warning',
  [TaskStatus.ON_HOLD]: 'warning',
  [TaskStatus.ON_APPROVAL]: 'warning',
  [TaskStatus.COMPLETED]: 'success',
  [TaskStatus.CANCELED]: 'danger',
  [TaskStatus.DENIED]: 'danger',
  [TaskStatus.FAILED]: 'danger',
};

const EVENT_STATUS_TONE: Record<string, StatusTone> = {
  planned: 'neutral',
  scheduled: 'neutral',
  on_hold: 'warning',
  in_progress: 'warning',
  underway: 'warning',
  ongoing: 'warning',
  completed: 'success',
  done: 'success',
  finished: 'success',
  canceled: 'danger',
  cancelled: 'danger',
  failed: 'danger',
};

const humanizeStatus = (status: string): string =>
  status
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

export const taskStatusView = (status: string): StatusView => ({
  label: TASK_STATUSES.find((option) => option.id === status)?.label ?? humanizeStatus(status),
  tone: TASK_STATUS_TONE[status] ?? 'neutral',
});

export const getItemStatusView = (item: CalendarItem): StatusView | null => {
  if (isCalendarAbsence(item)) return null;
  const raw = String(item.status ?? '').trim();
  if (!raw) return null;

  if (isCalendarTask(item)) return taskStatusView(raw);

  return { label: humanizeStatus(raw), tone: EVENT_STATUS_TONE[raw] ?? 'neutral' };
};

export const isCancelledItem = (item: CalendarItem): boolean => {
  if (isCalendarAbsence(item)) return false;
  const raw = String(item.status ?? '').toLowerCase();
  return raw === 'canceled' || raw === 'cancelled';
};

export const isCompletedItem = (item: CalendarItem): boolean => {
  if (isCalendarAbsence(item)) return false;
  const raw = String(item.status ?? '').toLowerCase();
  return raw === 'completed' || raw === 'done' || raw === 'finished';
};

export const notifyEventActionError = (error: unknown, action: 'cancel' | 'renew'): void => {
  const status = getErrorStatus(error);

  if (status === 409) {
    toast.error(`Couldn’t ${action} the event. Please refresh the calendar.`);
    return;
  }
  if (status === 403) {
    toast.error(`Couldn’t ${action} the event. Please ask an owner.`);
    return;
  }
  if (status && status >= 500) {
    toast.error(`Couldn’t ${action} the event. Please try again later.`);
    return;
  }
  toast.error(
    action === 'cancel'
      ? 'Couldn’t cancel the event. Please try again.'
      : 'Couldn’t renew the event. Please try again.',
  );
};

// Color Utilities

export const getItemBorderColor = (item: CalendarItem): string => {
  if (isCalendarAbsence(item)) return 'var(--accent-details-3)';
  if (isCalendarTask(item)) return 'var(--accent-details-2)';
  return EVENT_TYPE_COLORS[item.type] ?? 'var(--accent-details-1)';
};

// Event Transformation

export interface TransformedCalendarEvent {
  id: string;
  title: string;
  start?: string;
  end?: string;
  allDay: boolean;
  borderColor: string;
  extendedProps: CalendarItem;
}

export const isAllDayItem = (item: CalendarItem): boolean => item.time_slot?.all_day === true;

export const transformToCalendarEvents = (items: CalendarItem[]): TransformedCalendarEvent[] => {
  return items.map((item) => {
    const borderColor = getItemBorderColor(item);
    const start = item.time_slot?.start ? new Date(item.time_slot.start) : null;
    const end = item.time_slot?.end ? new Date(item.time_slot.end) : null;

    const isStartMidnight = start ? start.getHours() === 0 && start.getMinutes() === 0 : false;
    const isEndMidnight = end ? end.getHours() === 0 && end.getMinutes() === 0 : false;
    const isEndOfDay = end ? end.getHours() === 23 && end.getMinutes() === 59 : false;
    const spansMultipleDays = start && end ? !isSameDay(start, end) : false;
    const durationMs = start && end ? end.getTime() - start.getTime() : 0;
    const isAllDay =
      (isStartMidnight && (isEndMidnight || isEndOfDay)) ||
      (spansMultipleDays && durationMs >= 24 * 60 * 60 * 1000);

    let adjustedEnd = item.time_slot?.end;
    if (isAllDay && spansMultipleDays && end) {
      const nextDay = new Date(end);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(0, 0, 0, 0);
      adjustedEnd = nextDay.toISOString();
    }

    return {
      id: item.id,
      title: item.title,
      start: item.time_slot?.start,
      end: adjustedEnd,
      allDay: isAllDay,
      borderColor,
      extendedProps: item,
    };
  });
};

// Events for Date

export const getEventsForDate = (
  events: TransformedCalendarEvent[],
  date: Date,
): TransformedCalendarEvent[] => {
  // 'date' is a real local Date from FullCalendar — use local midnight boundaries
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  return events.filter((event) => {
    const originalItem = event.extendedProps;
    const rawStart = originalItem?.time_slot?.start ?? event.start ?? null;
    const rawEnd = originalItem?.time_slot?.end ?? event.end ?? null;

    if (!rawStart) return false;

    const start = new Date(rawStart);
    const end = rawEnd ? new Date(rawEnd) : start;

    const eventStart = new Date(start);
    eventStart.setHours(0, 0, 0, 0);
    const eventEnd = new Date(end);
    eventEnd.setHours(23, 59, 59, 999);

    return dayStart <= eventEnd && dayEnd >= eventStart;
  });
};

// Time Formatting

export const formatTimeRange = (start?: string, end?: string): string => {
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;

  if (!startDate) return 'Time not set';

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  const startText = timeFormatter.format(startDate);
  if (!endDate) return startText;

  return `${startText}-${timeFormatter.format(endDate)}`;
};

export const formatShortDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};
